import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { useSelector } from 'react-redux';

import { GlobalState } from 'reducers/stateTypes';
import { ZfBox, ZfInput, ZfSelectOption, ZfTypography } from '@zafire/react';

/**
 * AgGrid Custom Filter Props Interface
 */
interface IPartnerColumnFilterProps {
  filterChangedCallback?: () => void;
  api?: any;
  columnApi?: any;
  column?: any;
  registerFilter?: (filterInstance: any) => void;
}

/**
 * Custom AgGrid column filter for Partner selection
 * Provides server-side searchable dropdown (min 2 characters)
 */

const DROPDOWN_HEIGHT = 32;
export const AdditionalAttendeeFilter = forwardRef<any, IPartnerColumnFilterProps>((props, ref) => {
  const [selectedAttendee, setSelectedAttendee] = useState<string>('');
  const [employeeSuggestion, setEmployeeSuggestion] = useState('');
  const employeeOptions = useSelector((state: GlobalState) => state.resources.employeeOptions);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const { t } = useTranslation();

  // Register filter instance with parent component
  useEffect(() => {
    if (props.registerFilter) {
      // Pass the current component instance
      props.registerFilter({
        setModel: (model: any) => {
          if (model === null || model === undefined) {
            setSelectedAttendee('');
            setEmployeeSuggestion('');
          }
        },
      });
    }
  }, [props]);

  // Update dropdown position when showing
  useEffect(() => {
    if (employeeSuggestion.length > 0 && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    } else {
      setDropdownPosition(null);
    }
  }, [employeeSuggestion]);

  const employeeFilteredOptions = useMemo(() => {
    const suggestion = employeeSuggestion.toLowerCase();
    const data = employeeOptions.filter((option) => option?.label?.toLowerCase().includes(suggestion));
    return data?.length > 50 ? data.slice(0, 50) : data;
  }, [employeeOptions, employeeSuggestion]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        inputRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setEmployeeSuggestion('');
      }
    };

    if (dropdownPosition) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [dropdownPosition]);

  // AgGrid filter interface methods
  useImperativeHandle(ref, () => {
    return {
      // Called by AgGrid when it needs to know if filter is active
      isFilterActive() {
        return selectedAttendee !== null && selectedAttendee !== '';
      },

      // Called by AgGrid to get the filter model
      getModel() {
        if (selectedAttendee === '' || selectedAttendee === null) {
          return null;
        }
        return {
          filterType: 'text',
          type: 'equals',
          filter: selectedAttendee, // Partner UUID
        };
      },

      // Called by AgGrid to set the filter model (e.g., on filter restore or when reopening)
      setModel(model: any) {
        if (model === null || model === undefined) {
          setSelectedAttendee('');
          setEmployeeSuggestion(''); // Clear search input as well
        } else {
          // Restore selected partner from model
          if (model.filter) {
            // Try to find partner in current list
            const selectedItem = employeeOptions?.find((p) => p.value === model.filter);
            if (selectedItem) {
              setSelectedAttendee(selectedItem.value);
            } else {
              setSelectedAttendee('');
            }
          }
        }
      },

      // AgGrid calls this to see if component implements custom methods
      doesFilterPass() {
        return true;
      },
    };
  }, [selectedAttendee, employeeOptions]);

  const updateFilter = useCallback(
    (attendee: string) => {
      if (props.api && props.column) {
        try {
          // Get current filter model
          const currentFilterModel = props.api.getFilterModel() || {};

          // Build new filter model for this column
          const columnId = props.column.getColId();
          const newFilterModel = { ...currentFilterModel };

          if (attendee) {
            // Set filter for this column
            newFilterModel[columnId] = {
              filterType: 'text',
              type: 'equals',
              filter: attendee,
            };
          } else {
            // Remove filter for this column
            delete newFilterModel[columnId];
          }

          // Apply the new filter model
          props.api.setFilterModel(newFilterModel);

          // Now trigger the refresh
          if (props.api.refreshServerSide) {
            props.api.refreshServerSide({ purge: true });
          } else if (props.api.onFilterChanged) {
            props.api.onFilterChanged();
          }
          if (props.api.hideColumnFilter) {
            props.api.hideColumnFilter();
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[PartnerColumnFilter] Error setting filter model:', error);
        }
      }
    },
    [props.api, props.column],
  );

  return (
    <div className="ag-filter-wrapper" style={{ width: '100%' }}>
      <div className="ag-filter-body-wrapper ag-simple-filter-body-wrapper ag-focus-managed">
        {/* Filter Type Selector (disabled, always "equals") - matches AgGrid default picker field */}
        <ZfInput type="select" value="Equals" disabled getOptionLabel={() => 'Equals'}></ZfInput>

        {/* Filter Input - Searchable Dropdown */}
      </div>
      <ZfBox
        styles={{
          paddingLeft: 'var(--gap-horizontal-sm)',
          paddingRight: 'var(--gap-horizontal-sm)',
          paddingBottom: 'var(--gap-vertical-sm)',
        }}
      >
        <ZfInput
          name="selectedAttendee"
          type="select"
          onSelectChange={(e) => {
            setSelectedAttendee(e.detail as string);
          }}
          onInputDropdownOpen={() => setEmployeeSuggestion('')}
          onInputChange={(e) => setEmployeeSuggestion(e.detail)}
          placeholder={t('contactNote.home.filters.placeholder.search')}
          allowSearch
          value={selectedAttendee}
          getOptionLabel={(option) =>
            employeeOptions.find((co) => co.value === option.toLowerCase())?.label || `N/A (${option})`
          }
        >
          {employeeSuggestion !== '' && employeeFilteredOptions.length === 0 && (
            <ZfBox
              style={{
                padding: 'var(--gap-vertical-xs)',
                paddingBottom: 'var(--gap-vertical-xs)',
                paddingLeft: 'var(--gap-horizontal-md)',
              }}
            >
              <ZfTypography variant="body-small" color="normal">
                {t('contactNote.form.searchFail')}
              </ZfTypography>
            </ZfBox>
          )}
          {employeeSuggestion !== '' && employeeFilteredOptions.length > 0 && (
            <FixedSizeList
              itemSize={DROPDOWN_HEIGHT}
              itemCount={employeeFilteredOptions.length}
              overscanCount={5}
              height={Math.min(employeeFilteredOptions.length, 10) * DROPDOWN_HEIGHT}
              width="100%"
            >
              {(props: ListChildComponentProps) => {
                const { index, style } = props;
                const co = employeeFilteredOptions[index];

                return (
                  <ZfSelectOption
                    style={style}
                    key={co.value}
                    value={co.value}
                    isSelected={selectedAttendee === co.value}
                  >
                    {co.label}
                  </ZfSelectOption>
                );
              }}
            </FixedSizeList>
          )}
        </ZfInput>
      </ZfBox>
      <div className="ag-filter-apply-panel">
        <button
          className="ag-button ag-standard-button ag-filter-apply-panel-button ag-filter-apply-panel-apply-button"
          data-ref="applyFilterButton"
          type="submit"
          tabIndex={0}
          onClick={() => {
            updateFilter(selectedAttendee);
          }}
        >
          Apply
        </button>
        <button
          className="ag-button ag-standard-button ag-filter-apply-panel-button"
          data-ref="resetFilterButton"
          type="button"
          tabIndex={0}
          onClick={() => {
            setSelectedAttendee('');
            setEmployeeSuggestion('');
            updateFilter('');
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
});

AdditionalAttendeeFilter.displayName = 'AdditionalAttendeeFilter';

export default AdditionalAttendeeFilter;
